import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, ChevronRight, X, StickyNote, MapPin, Home } from "lucide-react";
import { api, formatApiError, gbp, floorLabel } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";

const STATUSES = ["draft", "matches_viewed", "products_selected", "service_requested", "awaiting_confirmation", "confirmed", "completed", "cancelled"];
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  matches_viewed: "bg-blue-100 text-blue-800",
  products_selected: "bg-indigo-100 text-indigo-800",
  service_requested: "bg-amber-100 text-amber-800",
  awaiting_confirmation: "bg-orange-100 text-orange-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function AdminProjects() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status) params.status = status;
    try {
      const r = await api.get("/admin/projects", { params });
      setRows(r.data.projects || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [status]); // eslint-disable-line

  return (
    <RetailerLayout>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-projects-page">
        <h1 className="font-display font-bold text-2xl text-slate-900 mb-4">Customer projects</h1>
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 grid sm:grid-cols-3 gap-3">
          <div className="relative sm:col-span-2">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="fm-input pl-9" placeholder="Reference, name, postcode…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="project-search" />
          </div>
          <select className="fm-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="project-filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? <div className="p-8 text-slate-500">Loading…</div>
            : rows.length === 0 ? <div className="p-8 text-center text-slate-500" data-testid="projects-empty">No customer projects yet.</div>
              : (
                <ul className="divide-y divide-slate-100" data-testid="projects-list">
                  {rows.map((p) => (
                    <li key={p.id}>
                      <button onClick={() => setSelected(p)} className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50" data-testid={`project-row-${p.id}`}>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{p.reference} · {p.project_name || "Untitled"}</div>
                          <div className="text-xs text-slate-500 truncate">{(p.rooms || []).length} rooms · {p.postcode || "—"} · {p.property_type || "—"} · {new Date(p.created_at).toLocaleDateString("en-GB")}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${STATUS_STYLE[p.status] || "bg-slate-100"}`}>{(p.status || "").replace(/_/g, " ")}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
        </div>
      </main>
      {selected && <ProjectDetail project={selected} onClose={() => setSelected(null)} onSaved={(u) => { setSelected(u); load(); }} />}
    </RetailerLayout>
  );
}

function ProjectDetail({ project, onClose, onSaved }) {
  const [p, setP] = useState(project);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function patch(body) {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/projects/${p.id}`, body);
      setP(r.data.project); setNote(""); onSaved(r.data.project); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  const s = p.summary || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" role="dialog" aria-modal="true" data-testid="project-detail-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900">{p.reference}</h3>
            <p className="text-xs text-slate-500">{p.project_name || "Untitled project"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-project-detail"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <span className="fm-label">Status</span>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="project-status">
              {STATUSES.map((st) => (
                <button key={st} onClick={() => patch({ status: st })} disabled={saving}
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 ${p.status === st ? (STATUS_STYLE[st] || "bg-slate-200") + " ring-2 ring-offset-1 ring-slate-400" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`set-project-status-${st}`}>{st.replace(/_/g, " ")}</button>
              ))}
            </div>
          </div>

          <Section title="Overview">
            <KV k="Property" v={p.property_type || "—"} />
            <KV k="Postcode" v={p.postcode || "—"} />
            <KV k="Service" v={p.service || "—"} />
            <KV k="Rooms" v={(p.rooms || []).length} />
            <KV k="Collection points" v={s.points ?? "—"} />
            <KV k="Flooring subtotal" v={s.subtotal != null ? gbp(s.subtotal) : "—"} />
            <KV k="Est. saving" v={s.saving != null ? gbp(s.saving) : "—"} />
          </Section>

          {(p.customer_name || p.customer_email || p.customer_phone) && (
            <Section title="Customer (private)">
              {p.customer_name && <KV k="Name" v={p.customer_name} />}
              {p.customer_email && <KV k="Email" v={p.customer_email} />}
              {p.customer_phone && <KV k="Phone" v={p.customer_phone} />}
            </Section>
          )}

          <Section title="Rooms">
            {(p.rooms || []).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm py-1">
                <span className="text-slate-700">{r.name} · {r.width_m}×{r.length_m}m</span>
                <span className="text-slate-500">{floorLabel(r.flooring_type)}</span>
              </div>
            ))}
          </Section>

          {(p.service_requirements && Object.keys(p.service_requirements).length > 0) && (
            <Section title="Service requirements">
              {Object.entries(p.service_requirements).map(([k, v]) => (
                <KV key={k} k={k.replace(/_/g, " ")} v={String(v)} />
              ))}
            </Section>
          )}

          <Section title="Attribution">
            <KV k="Source" v={p.source || "—"} />
            <KV k="Campaign" v={p.campaign || "—"} />
            <KV k="Updated" v={new Date(p.updated_at).toLocaleString("en-GB")} />
          </Section>

          <Section title="Private notes">
            <div className="space-y-2 mb-3">
              {(p.admin_notes || []).length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
              {(p.admin_notes || []).map((n, i) => (
                <div key={`${n.at || ""}-${i}`} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                  <p className="text-slate-800">{n.text}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{n.by} · {new Date(n.at).toLocaleString("en-GB")}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="fm-input flex-1" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="project-note-input" />
              <button onClick={() => note.trim() && patch({ note })} disabled={saving || !note.trim()} className="h-11 px-4 rounded-xl bg-[#0F3D2E] text-white font-semibold inline-flex items-center gap-1 disabled:opacity-60" data-testid="project-add-note">
                <StickyNote className="w-4 h-4" /> Add
              </button>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </section>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-500 capitalize">{k}</span>
      <span className="font-semibold text-slate-900 text-right capitalize">{v}</span>
    </div>
  );
}
